import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import {
  editEventCalendar,
  eventCalendarTypeList,
  viewEventCalendarDetails,
} from "../../../services/adminApiService/eventCalendarApis/eventCalendarApis";
import { formatDate } from "./EventCalendarModal";
import swal from "sweetalert";

const ALLOWED_CHARACTERS_REGEX = /^.*$/;

function EditEventCalendarModal({
  PreviewModalClose,
  PreviewEventModal,
  eventDocPreviewID,
  fetchData,
  currentpage,
}) {
  const [eventTypeList, seteventTypeList] = useState([]);
  const [formData, setFormData] = useState({
    important_documents: [],
    title: "",
    description: "",
    type_id: "",
    organized_by: "",
    start_date_time: "",
    end_date_time: "",
    location: "",
    remarks: "",
    video_link: "",
    attachement_name: "",
  });

  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const fileInputRef = React.useRef(null);

  const [importantDocFiles, setImportantDocFiles] = useState([]);
  const [importantDocPreviews, setImportantDocPreviews] = useState([
    null,
    null,
    null,
  ]);

  // Get today's date in YYYY-MM-DD format for min attribute
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const hours = String(today.getHours()).padStart(2, "0");
    const minutes = String(today.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Get min date for end date based on start date
  const getMinEndDate = () => {
    if (!formData.start_date_time) return getTodayDate();
    const startDate = new Date(formData.start_date_time);
    // Add 1 minute to ensure end date is after start date
    startDate.setMinutes(startDate.getMinutes() + 1);
    const year = startDate.getFullYear();
    const month = String(startDate.getMonth() + 1).padStart(2, "0");
    const day = String(startDate.getDate()).padStart(2, "0");
    const hours = String(startDate.getHours()).padStart(2, "0");
    const minutes = String(startDate.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Fetch event types
  const eventListHandler = async () => {
    try {
      const response = await eventCalendarTypeList();
      seteventTypeList(response?.data?.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    eventListHandler();
  }, []);

  // Fetch event details
  useEffect(() => {
    (async () => {
      try {
        let res = await viewEventCalendarDetails(eventDocPreviewID);
        const data = res?.data?.data || {};

        setFormData({
          title: data.title || "",
          description: data.description || "",
          type_id: data.type_id ? data.type_id.toString() : "",
          organized_by: data.organized_by || "",
          start_date_time: data.start_date_time || "",
          end_date_time: data.end_date_time || "",
          location: data.location || "",
          remarks: data.remarks || "",
          video_link: data.video_link || "",
          attachement_name: data.attachement_name || "",
          important_documents: data.important_documents
            ? (() => {
                try {
                  const docs = JSON.parse(data.important_documents);
                  if (docs.length === 1)
                    return [
                      ...docs,
                      { title: "", filename: "", link: "" },
                      { title: "", filename: "", link: "" },
                    ];
                  if (docs.length === 2)
                    return [...docs, { title: "", filename: "", link: "" }];
                  if (docs.length >= 3) return docs;
                  return [
                    { title: "", filename: "", link: "" },
                    { title: "", filename: "", link: "" },
                    { title: "", filename: "", link: "" },
                  ];
                } catch {
                  return [
                    { title: "", filename: "", link: "" },
                    { title: "", filename: "", link: "" },
                    { title: "", filename: "", link: "" },
                  ];
                }
              })()
            : [
                { title: "", filename: "", link: "" },
                { title: "", filename: "", link: "" },
                { title: "", filename: "", link: "" },
              ],
        });

        setFileUrl(data.attachement_storage_url || null);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [eventDocPreviewID]);

  // Handle input changes
  const handleChange = (e, idx = null) => {
    const { name, value } = e.target;

    if (name.startsWith("important_document_title_")) {
      setFormData((prev) => ({
        ...prev,
        important_documents: prev.important_documents.map((doc, docIdx) =>
          docIdx === idx ? { ...doc, title: value } : doc
        ),
      }));
    } else {
      // Handle date changes
      if (name === "start_date_time") {
        // If end date exists and is before new start date, clear end date
        if (formData.end_date_time && value > formData.end_date_time) {
          setFormData((prev) => ({
            ...prev,
            [name]: value,
            end_date_time: "",
          }));
          setErrors((prev) => ({
            ...prev,
            end_date_time: "End date must be after start date",
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            [name]: value,
          }));
          // Clear any previous errors
          setErrors((prev) => ({ ...prev, start_date_time: "" }));
        }
      } else if (name === "end_date_time") {
        // If start date doesn't exist, show error
        if (!formData.start_date_time) {
          setErrors((prev) => ({
            ...prev,
            end_date_time: "Please select start date first",
          }));
          return;
        }
        // Validate end date is after start date
        if (value && formData.start_date_time && value <= formData.start_date_time) {
          setErrors((prev) => ({
            ...prev,
            end_date_time: "End date must be after start date",
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            [name]: value,
          }));
          // Clear any previous errors
          setErrors((prev) => ({ ...prev, end_date_time: "" }));
        }
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    }
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};
    if (!formData.title) {
      newErrors.title = "Title is required.";
    } else if (!ALLOWED_CHARACTERS_REGEX.test(formData.title)) {
      newErrors.title = "Title contains invalid characters.";
    }
    if (!formData.description) newErrors.description = "Description is required.";
    if (!formData.type_id) newErrors.type_id = "Type is required.";

    // Date validations
    if (formData.start_date_time) {
      const startDate = new Date(formData.start_date_time);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of day for comparison

      if (startDate < today) {
        newErrors.start_date_time = "Start date cannot be before today.";
      }
    }

    if (formData.end_date_time && !formData.start_date_time) {
      newErrors.end_date_time = "Start date is required when the end date is selected.";
    }

    if (formData.start_date_time && formData.end_date_time) {
      const startDate = new Date(formData.start_date_time);
      const endDate = new Date(formData.end_date_time);
      if (startDate >= endDate) {
        newErrors.end_date_time = "End date must be after start date.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit
  const handleSubmit = async () => {
    if (validateForm()) {
      const updatedFormData = {
        ...formData,
        start_date_time:
          formData.start_date_time && formatDate(formData.start_date_time),
        end_date_time:
          formData.end_date_time && formatDate(formData.end_date_time),
        attachement: selectedFile || null,
      };

      const newFormData = new FormData();

      // Append normal fields (exclude attachement + important_documents)
      Object.keys(updatedFormData).forEach((key) => {
        if (updatedFormData[key] !== undefined && updatedFormData[key] !== null) {
          if (key !== "attachement" && key !== "important_documents") {
            newFormData.append(key, updatedFormData[key]);
          }
        }
      });

      // Attachment file
      if (selectedFile) {
        newFormData.append("attachement", selectedFile);
      }

      // Important docs (titles + files)
      updatedFormData.important_documents?.forEach((doc, idx) => {
        if (doc.title) {
          newFormData.append(`important_document_title_${idx}`, doc.title);
        }
        if (importantDocFiles[idx]) {
          newFormData.append(`important_documents[${idx}]`, importantDocFiles[idx]);
        }
      });

      // Event ID
      newFormData.append("id", eventDocPreviewID);

      try {
        let res = await editEventCalendar(newFormData);
        if (res.data.status && res.data.status !== "error") {
          swal(res.data.message, "", "success");
          // ✅ Update local formData state with latest file info
          setFormData((prev) => ({
            ...prev,
            important_documents: updatedFormData.important_documents.map(
              (doc, idx) => ({
                ...doc,
                filename: importantDocFiles[idx]?.name || doc.filename,
              })
            ),
          }));
          PreviewModalClose();
          fetchData({ currentpage });
        } else {
          swal(`${res.data.status}`, "", "error");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        swal(`${error.message}`, "", "error");
      }
    }
  };

  // Handle main attachment
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      const allowedExtensions = /(\.jpg|\.jpeg|\.webp|\.png|\.pdf)$/i;
      const maxFileSize = 10 * 1024 * 1024;

      if (!allowedExtensions.test(file.name)) {
        swal("Error", "Invalid file type.", "error");
        setSelectedFile(null);
        setFileUrl(null);
        return;
      }

      if (file.size > maxFileSize) {
        swal("Error", "File size should be less than 10 MB.", "error");
        setSelectedFile(null);
        setFileUrl(null);
        return;
      }

      setSelectedFile(file);
      setFileUrl(URL.createObjectURL(file));
      swal("", "File is selected", "success");
    } else {
      setSelectedFile(null);
      setFileUrl(null);
    }
  };

  // Handle important document files
  const handleImportantDocFileChange = (idx, e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      const allowedExtensions = /(\.jpg|\.jpeg|\.webp|\.png|\.pdf)$/i;
      const maxFileSize = 10 * 1024 * 1024;

      if (!allowedExtensions.test(file.name)) {
        swal("Error", "Invalid file type.", "error");
        return;
      }
      if (file.size > maxFileSize) {
        swal("Error", "File size should be less than 10 MB.", "error");
        return;
      }

      const updatedFiles = [...importantDocFiles];
      updatedFiles[idx] = file;
      setImportantDocFiles(updatedFiles);

      const updatedPreviews = [...importantDocPreviews];
      updatedPreviews[idx] = URL.createObjectURL(file);
      setImportantDocPreviews(updatedPreviews);

      swal("", "File is selected", "success");
    }
  };

  return (
    <Modal show={PreviewEventModal} onHide={PreviewModalClose}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Event Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="add-contact-box">
          <div className="add-contact-content">
            {/* Title */}
            <div className="row">
              <div className="form-group mb-3 col-md-12">
                <label className="text-black font-w500">Title</label>
                <span className="text-danger">*</span>
                <Form.Control
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Title"
                  isInvalid={!!errors.title}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.title}
                </Form.Control.Feedback>
              </div>
            </div>

            {/* Description */}
            <div className="row">
              <div className="form-group mb-3 col-md-12">
                <label className="text-black font-w500">Description</label>
                <span className="text-danger">*</span>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Description"
                  isInvalid={!!errors.description}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.description}
                </Form.Control.Feedback>
              </div>
            </div>

            {/* Type & Organized By */}
            <div className="row">
              <div className="form-group mb-3 col-md-6">
                <label className="text-black font-w500">Type</label>
                <span className="text-danger">*</span>
                <select
                  required
                  name="type_id"
                  value={formData.type_id}
                  onChange={handleChange}
                  className={`form-control ${
                    errors.type_id ? "is-invalid" : ""
                  }`}
                >
                  <option value="">Select type</option>
                  {eventTypeList.map((data) => (
                    <option key={data?.id} value={data?.id}>
                      {data?.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group mb-3 col-md-6">
                <label className="text-black font-w500">Organized By</label>
                <Form.Control
                  name="organized_by"
                  value={formData.organized_by}
                  placeholder="Enter Organization name"
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Dates */}
            <div className="row">
              <div className="form-group mb-3 col-md-6">
                <label className="text-black font-w500">Start Date</label>
                <Form.Control
                  type="datetime-local"
                  name="start_date_time"
                  value={formData.start_date_time}
                  onChange={handleChange}
                  isInvalid={!!errors.start_date_time}
                  min={getTodayDate()} // Cannot select dates before today
                />
                <Form.Control.Feedback type="invalid">
                  {errors.start_date_time}
                </Form.Control.Feedback>
              </div>
              <div className="form-group mb-3 col-md-6">
                <label className="text-black font-w500">End Date</label>
                <Form.Control
                  type="datetime-local"
                  name="end_date_time"
                  value={formData.end_date_time}
                  onChange={handleChange}
                  isInvalid={!!errors.end_date_time}
                  min={getMinEndDate()} // Must be after start date
                  disabled={!formData.start_date_time} // Disabled until start date is selected
                />
                <Form.Control.Feedback type="invalid">
                  {errors.end_date_time}
                </Form.Control.Feedback>
              </div>
            </div>

            {/* Location & Remarks */}
            <div className="row">
              <div className="form-group mb-3 col-md-6">
                <label className="text-black font-w500">Location</label>
                <Form.Control
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Location"
                  isInvalid={!!errors.location}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.location}
                </Form.Control.Feedback>
              </div>
              <div className="form-group mb-3 col-md-6">
                <label className="text-black font-w500">Remarks</label>
                <Form.Control
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  placeholder="Remarks"
                />
              </div>
            </div>

            {/* Main Document */}
            <div className="row  ">
              <div className="form-group mb-3 col-md-12">
                <Form.Group controlId="formFile">
                  <Form.Label className="text-black font-w500">
                    Upload Document
                  </Form.Label>
                  <div className="d-flex align-items-center relative">
                    <Form.Control
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleFileChange}
                    />

                    <span
                      style={{
                        color: "#6B7280",
                        overflow: "hidden",
                        position: "absolute",
                        left: "35px",
                        top: "52%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      {selectedFile
                        ? selectedFile.name
                        : formData.attachement_name || "No file chosen"}
                    </span>
                    <Button
                      variant="primary"
                      className="ms-2"
                      disabled={!(selectedFile || fileUrl)}
                      onClick={() => {
                        if (selectedFile) {
                          window.open(fileUrl, "_blank");
                        } else if (fileUrl) {
                          window.open(fileUrl, "_blank");
                        }
                      }}
                    >
                      Preview
                    </Button>
                  </div>
                </Form.Group>
              </div>
            </div>

            {/* Video link */}
            <div className="row mt-1">
              <div className="form-group mb-3 col-md-12">
                <Form.Group controlId="formVideoLink">
                  <Form.Label className="text-black font-w500">
                    Upload Video Link
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="video_link"
                    placeholder="Paste video link here"
                    value={formData.video_link || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
              </div>
            </div>

            {/* Important Documents */}
            <div className="row">
              <div className="form-group mb-3 col-md-12">
                <Form.Label className="text-black font-w500">
                  Upload Important Document
                </Form.Label>
                {formData?.important_documents?.map((el, idx) => (
                  <div
                    key={idx}
                    className="row mb-3 p-2 border rounded align-items-center gx-2 gy-1"
                  >
                    <div className="col-12 col-md-4">
                      <Form.Control
                        type="text"
                        name={`important_document_title_${idx}`}
                        placeholder="Title"
                        value={el?.title || ""}
                        onChange={(e) => handleChange(e, idx)}
                      />
                    </div>
                    <div className="col-12 col-md-5 mt-4">
                      <Form.Control
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => handleImportantDocFileChange(idx, e)}
                      />
                      <span
                        style={{
                          marginLeft: 5,
                          color: "#6B7280",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 150,
                        }}
                      >
                        {el?.filename}
                      </span>
                    </div>
                    <div className="col-12 col-md-3 d-flex justify-content-md-start justify-content-center mt-2 mt-md-0">
                      <Button
                        variant="primary"
                        className="ms-md-2 btn-sm"
                        disabled={!(el?.link || importantDocPreviews[idx])} // ✅ enable if link OR preview exists
                        onClick={() => {
                          if (importantDocPreviews[idx]) {
                            // ✅ Local preview (for newly uploaded file)
                            window.open(importantDocPreviews[idx], "_blank");
                          } else if (el?.link) {
                            // ✅ Backend preview (for existing file)
                            const finalUrl = el?.link.startsWith("http")
                              ? el?.link
                              : `http://staging-api-admin.khelouk.in/${el?.link}`;
                            window.open(finalUrl, "_blank");
                          }
                        }}
                      >
                        Preview
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={PreviewModalClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          Update Event
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditEventCalendarModal;